import { Injectable } from "@angular/core";
import { NgxPermissionsService, NgxRolesService } from "ngx-permissions";
import { AvailableAccount } from "./accounts/AvailableAccount";
import { RoleDTO } from "./roles/Role";
import { UserDTO } from "./auth/User";
import { PermissionsHandlerService } from "./handlers/permissions-handler.service";
import { RolesHandlerService } from "./handlers/roles-handler.service";
import { BehaviorSubject } from "rxjs";
import {
  FEATURE_AURA_KEY,
  FEATURE_EXPLORIST_KEY,
  FEATURE_LUMA_KEY,
  FEATURE_TEMPO_KEY,
  FEATURE_TRIPIST_KEY,
} from "./features.constants";

@Injectable({
  providedIn: "root",
})
export class PermissionService {
  private _roles: any = [];
  categories!: {
    users: PermissionCategory;
    accounts: PermissionCategory;
    accountUsers: PermissionCategory;
    roles: PermissionCategory;
    permissions: PermissionCategory;
    broadcasts: PermissionCategory;
    templates: PermissionCategory;
    media: PermissionCategory;
    accountApps: PermissionCategory;
    logicTrees: PermissionCategory;
    geofences: PermissionCategory;
    beaconRegions: PermissionCategory;
    accountAppUsers: PermissionCategory;
    accountSignalCategories: PermissionCategory;
    facilities: PermissionCategory;
    beacons: PermissionCategory;
    integrations: PermissionCategory;
    assististCategories: PermissionCategory;
    assististDocuments: PermissionCategory;
    luma: LumaPermissionCategory;
  };
  private permissionsLoadedSubject = new BehaviorSubject(false);
  permissionsLoadedObs = this.permissionsLoadedSubject.asObservable();
  private superUser: boolean = false;
  constructor(
    private NgxRolesService: NgxRolesService,
    private RolesHandlerService: RolesHandlerService,
    private PermissionsHandlerService: PermissionsHandlerService,
    private NgxPermissionsService: NgxPermissionsService,
  ) {}

  get roles() {
    return this._roles;
  }

  get permissionsLoaded() {
    return this.permissionsLoadedSubject.value;
  }

  //TODO: Revisit this service
  initRoles(user: UserDTO) {
    user.permissions.forEach((permission) => {
      permission.hasAnyRole?.forEach((role: any) => {
        const roleUpper = role.toUpperCase();
        const roleObj = this._roles.find(
          (roleObj: any) => roleObj.code === roleUpper,
        );
        if (roleObj) {
          roleObj.permissions = [...roleObj.permissions, permission.name];
        } else {
          this._roles.push({
            code: roleUpper,
            role,
            name: this.getRoleName(role),
            permissions: [permission.name],
          });
        }
      });
    });
  }

  clearRole() {
    this.NgxRolesService.flushRolesAndPermissions();
  }

  setRole(account: AvailableAccount, currentUser: UserDTO) {
    if (!currentUser?.super) {
      const accountUserrole = account?.role?.code.toUpperCase();
      if (accountUserrole) {
        this.NgxRolesService.addRoleWithPermissions(
          accountUserrole,
          this._roles.find((role: any) => role.code === accountUserrole)
            ?.permissions ?? [],
        );
      }
    }
    this.initPermissionCategories(currentUser);
    this.superUser = currentUser?.super;
    this.permissionsLoadedSubject.next(true);
  }

  initPermissionCategories(userDTO: UserDTO) {
    this.categories = {
      users: new PermissionCategory(
        "users",
        this.NgxPermissionsService,
        userDTO,
      ),
      accounts: new PermissionCategory(
        "accounts",
        this.NgxPermissionsService,
        userDTO,
      ),
      accountUsers: new PermissionCategory(
        "account_users",
        this.NgxPermissionsService,
        userDTO,
      ),
      roles: new PermissionCategory(
        "roles",
        this.NgxPermissionsService,
        userDTO,
      ),
      permissions: new PermissionCategory(
        "permissions",
        this.NgxPermissionsService,
        userDTO,
      ),
      broadcasts: new PermissionCategory(
        "quicksignal",
        this.NgxPermissionsService,
        userDTO,
      ),
      templates: new PermissionCategory(
        "signal-template",
        this.NgxPermissionsService,
        userDTO,
      ),
      media: new PermissionCategory(
        "media",
        this.NgxPermissionsService,
        userDTO,
      ),
      accountApps: new PermissionCategory(
        "accountApps",
        this.NgxPermissionsService,
        userDTO,
      ),
      logicTrees: new PermissionCategory(
        "logicTrees",
        this.NgxPermissionsService,
        userDTO,
      ),
      geofences: new PermissionCategory(
        "geofences",
        this.NgxPermissionsService,
        userDTO,
      ),
      beaconRegions: new PermissionCategory(
        "beaconRegions",
        this.NgxPermissionsService,
        userDTO,
      ),
      accountAppUsers: new PermissionCategory(
        "accountAppUser",
        this.NgxPermissionsService,
        userDTO,
      ),
      accountSignalCategories: new PermissionCategory(
        "account-signal-category",
        this.NgxPermissionsService,
        userDTO,
      ),
      facilities: new PermissionCategory(
        "facilities",
        this.NgxPermissionsService,
        userDTO,
      ),
      beacons: new PermissionCategory(
        "beacons",
        this.NgxPermissionsService,
        userDTO,
      ),
      integrations: new PermissionCategory(
        "integrations",
        this.NgxPermissionsService,
        userDTO,
      ),
      assististCategories: new PermissionCategory(
        "document_categories",
        this.NgxPermissionsService,
        userDTO,
      ),
      assististDocuments: new PermissionCategory(
        "documents",
        this.NgxPermissionsService,
        userDTO,
      ),
      luma: new LumaPermissionCategory(
        "luma.campaigns",
        this.NgxPermissionsService,
        userDTO,
      ),
    };
  }

  private getRoleName(role: string) {
    let roleName = "";
    let afterUnderScore = false;
    for (let i = 0; i < role.length; i++) {
      if (i === 0 || afterUnderScore) {
        roleName = roleName + role.charAt(i).toUpperCase();
        afterUnderScore = false;
      } else if (role.charAt(i) === "_") {
        roleName = roleName + " ";
        afterUnderScore = true;
      } else {
        roleName = roleName + role.charAt(i).toLowerCase();
      }
    }

    return roleName;
  }

  hasRole(roleName: string) {
    if (this.superUser) {
      return true;
    } else {
      roleName = roleName.toUpperCase();

      const roles = this.NgxRolesService.getRoles();
      for (let roleKey of Object.keys(roles)) {
        if (roles[roleKey].name === roleName) {
          return true;
        }
      }

      return false;
    }
  }

  getPermissions(masterAccountID: number) {
    return this.PermissionsHandlerService.getPermissions(masterAccountID);
  }

  getRoles(accountID: number, hideDeleted?: boolean) {
    return this.RolesHandlerService.getRoles(accountID, hideDeleted);
  }

  getRole(masterAccountID: number, roleID: number) {
    return this.RolesHandlerService.getRole(masterAccountID, roleID);
  }

  deleteRole(masterAccountID: number, roleID: number) {
    return this.RolesHandlerService.deleteRole(masterAccountID, roleID);
  }

  updateRole(masterAccountID: number, roleID: number, role: RoleDTO) {
    return this.RolesHandlerService.updateRole(masterAccountID, roleID, role);
  }

  createRole(masterAccountID: number, role: RoleDTO) {
    return this.RolesHandlerService.createRole(masterAccountID, role);
  }

  /**
   * Checks if the current account (or provided accountValue) has a feature with the given key.
   *
   * NOTE: `featureKey` MUST match the backend `features.key` exactly. See `features.constants.ts`.
   */
  hasFeature(featureKey: string, accountValue?: any): boolean {
    const features = accountValue
      ? accountValue.features
      : (this as any).sessionService?.currentAccountValue?.features;
    return (
      Array.isArray(features) &&
      features.some((f: any) => {
        // Prefer `key`, but gracefully fall back to `name` while the backend migrates.
        const key = (f as any).key ?? (f as any).name;
        return key === featureKey;
      })
    );
  }

  /**
   * Checks if a specific widget type is available based on account features.
   * Weather widget is always available, all others require LHPE feature.
   */
  isWidgetAvailable(widgetType: string, accountValue?: any): boolean {
    // Weather widget is always available
    if (widgetType === 'weather') {
      return true;
    }
    
    // Assistant Threads widget requires Tripist or Explorist feature
    if (widgetType === 'assistant_threads') {
      return (
        this.hasFeature(FEATURE_TRIPIST_KEY, accountValue) ||
        this.hasFeature(FEATURE_EXPLORIST_KEY, accountValue) ||
        this.hasFeature(FEATURE_AURA_KEY, accountValue)
      );
    }
    
    // Assistant Devices/Users widget requires Tripist or Explorist feature
    if (widgetType === 'assistant_devices_users') {
      return (
        this.hasFeature(FEATURE_TRIPIST_KEY, accountValue) ||
        this.hasFeature(FEATURE_EXPLORIST_KEY, accountValue) ||
        this.hasFeature(FEATURE_AURA_KEY, accountValue)
      );
    }
    
    // Assistant Shares/Ratings/Feedback widget requires Tripist or Explorist feature
    if (widgetType === 'assistant_shares_ratings_feedback') {
      return (
        this.hasFeature(FEATURE_TRIPIST_KEY, accountValue) ||
        this.hasFeature(FEATURE_EXPLORIST_KEY, accountValue) ||
        this.hasFeature(FEATURE_AURA_KEY, accountValue)
      );
    }
    
    // All other widgets require Tempo feature
    return this.hasFeature(FEATURE_TEMPO_KEY, accountValue);
  }

  /**
   * Gets the list of available widget types for the current account.
   */
  getAvailableWidgetTypes(accountValue?: any): string[] {
    const allWidgetTypes = [
      'weather',
      'deliveries_by_category', 
      'installs',
      'mobile_location_allowed',
      'regions_snapshot',
      'on_property',
      'delivered_content',
      'assistant_threads',
      'assistant_devices_users',
      'assistant_shares_ratings_feedback',
      'generic'
    ];

    return allWidgetTypes.filter(widgetType => 
      this.isWidgetAvailable(widgetType, accountValue)
    );
  }
}

export class PermissionCategory {
  constructor(
    private _category: string,
    protected NgxPermissionsService: NgxPermissionsService,
    protected UserDTO: UserDTO,
  ) {}

  hasList() {
    return this.UserDTO.super
      ? Promise.resolve(this.UserDTO.super)
      : this.NgxPermissionsService.hasPermission(`${this.category}.list`);
  }

  hasDetail() {
    return this.UserDTO.super
      ? Promise.resolve(this.UserDTO.super)
      : this.NgxPermissionsService.hasPermission(`${this.category}.detail`);
  }

  hasWrite() {
    return this.UserDTO.super
      ? Promise.resolve(this.UserDTO.super)
      : this.NgxPermissionsService.hasPermission(`${this.category}.write`);
  }

  hasCreate() {
    return this.UserDTO.super
      ? Promise.resolve(this.UserDTO.super)
      : this.NgxPermissionsService.hasPermission(`${this.category}.create`);
  }

  hasRemove() {
    return this.UserDTO.super
      ? Promise.resolve(this.UserDTO.super)
      : this.NgxPermissionsService.hasPermission(`${this.category}.remove`);
  }

  hasDelete() {
    return this.UserDTO.super
      ? Promise.resolve(this.UserDTO.super)
      : this.NgxPermissionsService.hasPermission(`${this.category}.delete`);
  }

  hasReport() {
    return this.UserDTO.super
      ? Promise.resolve(this.UserDTO.super)
      : this.NgxPermissionsService.hasPermission(`${this.category}.report`);
  }

  get category() {
    return this._category;
  }
}

export class LumaPermissionCategory extends PermissionCategory {
  constructor(
    category: string,
    NgxPermissionsService: NgxPermissionsService,
    UserDTO: UserDTO,
  ) {
    super(category, NgxPermissionsService, UserDTO);
  }

  hasSend() {
    return this.UserDTO.super
      ? Promise.resolve(this.UserDTO.super)
      : this.NgxPermissionsService.hasPermission(`${this.category}.send`);
  }
}
